import type {
	CourseMindItemSearchResult,
	ItemVariation,
	MindItemSearchResult,
} from '@kimuno/api-sdk';
import immutable from 'object-path-immutable';

import { LOGOUT, type LogoutAction } from '../actions/auth.actionTypes';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import { FETCH_NEW_COURSES, type FetchNewCoursesAction } from '../actions/course.actionTypes';
import {
	FETCH_DASHBOARD_WORKSHEETS,
	type FetchDashboardWorksheetsAction,
} from '../actions/dashboard.actionTypes';
import {
	PIN_MIND_ITEM,
	type PinMindItemAction,
	UNPIN_DOCUMENT,
	type UnpinDocumentAction,
} from '../actions/pinned.actionTypes';
import {
	FETCH_PRODUCTS_BY_QUERY,
	type FetchProductsByQueryAction,
} from '../actions/product.actionTypes';

type ActionTypes =
	| FetchProductsByQueryAction
	| LogoutAction
	| FetchNewCoursesAction
	| FetchDashboardWorksheetsAction
	| PinMindItemAction
	| UnpinDocumentAction;

export interface DashboardInitialStateType {
	productsBySubjectId: {
		[key: string]: {
			ids: string[];
			error?: string;
			status: AsyncActionStatus;
		};
	};
	newCourses: {
		data: CourseMindItemSearchResult[];
		status: AsyncActionStatus;
	};
	dashboardWorksheets: {
		data: MindItemSearchResult[];
		status: AsyncActionStatus;
		error?: string;
		// Joined subject key the stored data belongs to; lets the row refetch on a
		// subject change while skipping a remount that already holds the same set.
		subjectsKey: string;
	};
}

// Mirrors `getSubjectProducts`' key (`${prefix}-${subjectId}`). A query that
// omits `subjects` entirely is the publisher-only Studyflix level (PRO-141) and
// keys on the keyPrefix alone. An *empty* subjects array keeps the legacy
// contract: such fetches (e.g. StudyflixCarousel with no user subjects) were
// never stored, and keying them now would change control-cohort behavior.
const productsQueryKey = (query: { subjects?: string[]; keyPrefix?: string }): string | null => {
	if (query.subjects) {
		if (query.subjects.length === 0) return null;
		const subjectsKey = query.subjects.join(',');
		return query.keyPrefix ? `${query.keyPrefix}-${subjectsKey}` : subjectsKey;
	}
	return query.keyPrefix ? `${query.keyPrefix}-` : null;
};

// Optimistic pin flip for the dashboard-scoped worksheet slice, mirroring
// `mindItems.reducers.ts`. Bails out with the original state when nothing matches:
// `getDashboardWorksheets` is unmemoized, so a fresh `data` array would re-render the
// worksheet row for every pin made anywhere in the app.
const setWorksheetPinned = (
	state: DashboardInitialStateType,
	payload: { id: string; type: ItemVariation },
	isPinned: boolean
): DashboardInitialStateType => {
	const { data } = state.dashboardWorksheets;

	if (payload.type === 'product' || !data.some((item) => item.id === payload.id)) {
		return state;
	}

	return immutable(state)
		.set(
			['dashboardWorksheets', 'data'],
			data.map((item) => (item.id === payload.id ? { ...item, isPinned } : item))
		)
		.value();
};

const initialState: DashboardInitialStateType = {
	productsBySubjectId: {},
	newCourses: {
		data: [],
		status: AsyncActionStatus.INITIAL,
	},
	dashboardWorksheets: {
		data: [],
		status: AsyncActionStatus.INITIAL,
		subjectsKey: '',
	},
};

// biome-ignore lint/style/useDefaultParameterLast: parameter order is a fixed contract (Redux reducer signature / existing call sites)
const collections = (state = initialState, action: ActionTypes): DashboardInitialStateType => {
	switch (action.type) {
		case LOGOUT.SUCCESS:
			return initialState;

		case FETCH_PRODUCTS_BY_QUERY.START: {
			const key = productsQueryKey(action.payload.query);
			if (key === null) {
				return state;
			}

			return immutable(state)
				.set(['productsBySubjectId', key, 'status'], AsyncActionStatus.LOADING)
				.set(['productsBySubjectId', key, 'ids'], [])
				.value();
		}
		case FETCH_PRODUCTS_BY_QUERY.ERROR: {
			const key = productsQueryKey(action.payload.query);
			if (key === null) {
				return state;
			}

			return immutable(state)
				.set(['productsBySubjectId', key, 'status'], AsyncActionStatus.ERROR)
				.value();
		}
		case FETCH_PRODUCTS_BY_QUERY.SUCCESS: {
			const key = productsQueryKey(action.payload.query);
			if (key === null) {
				return state;
			}

			return immutable(state)
				.set(['productsBySubjectId', key, 'status'], AsyncActionStatus.READY)
				.set(
					['productsBySubjectId', key, 'ids'],
					action.result.map((r) => r.id)
				)
				.value();
		}

		case FETCH_NEW_COURSES.START: {
			return immutable(state)
				.set('newCourses', {
					data: [],
					status: AsyncActionStatus.LOADING,
				})
				.value();
		}

		case FETCH_NEW_COURSES.ERROR: {
			return immutable(state)
				.set('newCourses', {
					data: [],
					status: AsyncActionStatus.ERROR,
				})
				.value();
		}

		case FETCH_NEW_COURSES.SUCCESS: {
			const { result } = action;

			return immutable(state)
				.set('newCourses', {
					data: result.data,
					status: AsyncActionStatus.READY,
				})
				.value();
		}

		case FETCH_DASHBOARD_WORKSHEETS.START: {
			return immutable(state)
				.set('dashboardWorksheets', {
					data: [],
					status: AsyncActionStatus.LOADING,
					subjectsKey: action.payload.subjects.join(','),
				})
				.value();
		}

		case FETCH_DASHBOARD_WORKSHEETS.ERROR: {
			// Ignore an out-of-order response whose subjects no longer match the latest
			// START — a newer request already owns the slot; clobbering it would surface
			// stale data and trigger a redundant refetch.
			if (action.payload.subjects.join(',') !== state.dashboardWorksheets.subjectsKey) {
				return state;
			}

			return immutable(state)
				.set('dashboardWorksheets', {
					data: [],
					status: AsyncActionStatus.ERROR,
					subjectsKey: action.payload.subjects.join(','),
				})
				.value();
		}

		case FETCH_DASHBOARD_WORKSHEETS.SUCCESS: {
			// Same stale-response guard as ERROR: a later START for different subjects
			// owns the slot, so drop this superseded result.
			if (action.payload.subjects.join(',') !== state.dashboardWorksheets.subjectsKey) {
				return state;
			}

			const seen = new Set<string>();
			const data = action.result.data.filter((item) => {
				if (seen.has(item.id)) return false;
				seen.add(item.id);
				return true;
			});

			return immutable(state)
				.set('dashboardWorksheets', {
					data,
					status: AsyncActionStatus.READY,
					subjectsKey: action.payload.subjects.join(','),
				})
				.value();
		}

		case PIN_MIND_ITEM.START:
		case UNPIN_DOCUMENT.ERROR:
			return setWorksheetPinned(state, action.payload, true);

		case PIN_MIND_ITEM.ERROR:
		case UNPIN_DOCUMENT.START:
			return setWorksheetPinned(state, action.payload, false);

		case PIN_MIND_ITEM.SUCCESS: {
			const { id } = action.payload;

			const newCourses = state.newCourses.data.map((course) => {
				if (course.id === id) {
					return {
						...course,
						isPinned: true,
					};
				}
				return course;
			});

			return immutable(state)
				.set('newCourses', {
					data: newCourses,
					status: AsyncActionStatus.READY,
				})
				.value();
		}

		case UNPIN_DOCUMENT.SUCCESS: {
			const { id } = action.payload;

			const newCourses = state.newCourses.data.map((course) => {
				if (course.id === id) {
					return {
						...course,
						isPinned: false,
					};
				}
				return course;
			});

			return immutable(state)
				.set('newCourses', {
					data: newCourses,
					status: AsyncActionStatus.READY,
				})
				.value();
		}
	}

	return state;
};

export default collections;
